import { defineStore } from 'pinia'
import { getMenuListForAll, getMenuListForUser } from '@/pages/layout/services'
import { transformList } from '@/utils'
import { flattenAndObject } from '@/utils/route-menu'
import lodash from 'lodash'

import { showUpdateInfo, reloadFlagKey } from '@/utils/cache-opt'

const storeKey = 'gattaran-workshop-store-menu'
const useMenuStore = defineStore(storeKey, {
  persist: [
    // 可以设置多个配置的
    {
      key: storeKey,
      storage: window.localStorage,
      // 优化 针对菜单的原始数据进行缓存处理
      // 注意 引用不会持久化。
      paths: ['originMenuListAll', 'originMenuList'],
      afterRestore: ctx => {
        console.log(`--> just restored '${ctx.store.$id}'`)
      },
    },
  ],
  state: () => {
    return {
      // 存储原始数据,服务端下发的数据
      originMenuList: [], // 有权限菜单的
      originMenuListAll: [], // 全量菜单数据

      // 路由菜单信息 转路由格式的，可以直接动态添加路由
      routeList: [], // 权限路由
      routeListAll: [], // 全量路由。
      // 路由打平
      routeFlattenObject: {}, // 权限路由打平
      routeAllFlattenObject: {}, // 全量路由打平
      // 当前菜单信息
      currentSideList: [], // 转换后的格式
    }
  },
  // getters有缓存功能
  getters: {
    getCurrentAsyncSideList: state => {
      return state.currentSideList?.filter(i => i?.meta?.isAsync) || []
    },
    // 筛选无权限菜单
    routeOmitFlattenObject(state) {
      return lodash.omit(state.routeAllFlattenObject, Object.keys(state.routeFlattenObject))
    },
    getRouteFlattenObject: state => state.routeFlattenObject,
    // 用户概述页数据查询
    getAllSideMenuMap: state => {
      return state.routeListAll.reduce((pre, next) => {
        if (!next.hidden) {
          pre[next.name] = next
        }
        return pre
      }, {})
    },
    // 根据nodeKey获取路由详细信息
    getRouteInfo: state => {
      return nodeKey => {
        if (!state.routeFlattenObject[nodeKey]) console.warn(`${nodeKey} not found!!`)
        return state.routeFlattenObject[nodeKey] || null
      }
    },
  },
  actions: {
    getSideListByKey(key) {
      return this.routeList.find(i => i.key === key)?.children()
    },
    /**
     * 初始化权限菜单相关数据
     * @returns {Promise<boolean>}
     */
    async initMenuList() {
      const next = list => {
        const newList = transformList(lodash.cloneDeep(list))
        this.routeList = newList
        this.routeFlattenObject = flattenAndObject(newList)
      }
      const getOriginMenuList = async isCache => {
        const res = await getMenuListForUser()
        // 判断是否相同，然后提示用户刷新
        const oldList = lodash.cloneDeep(this.originMenuList)
        const isSame = isMenuListSame(res, oldList)
        if (isCache && !isSame) {
          showUpdateInfo('权限菜单有更新')
        }
        this.originMenuList = res
        next(res)
        return true
      }
      try {
        // 命中缓存
        if (this.originMenuList.length > 0 && !sessionStorage.getItem(reloadFlagKey)) {
          next(this.originMenuList)
          getOriginMenuList(true)
          return true
        }
        await getOriginMenuList()
        return true
      } catch (e) {
        console.error(e)
      }
    },
    /**
     * 初始化全量菜单相关数据
     * @returns {Promise<boolean>}
     */
    async getMenuListAll() {
      const next = list => {
        const newList = transformList(lodash.cloneDeep(list))
        this.routeListAll = newList
        this.routeAllFlattenObject = flattenAndObject(newList)
      }
      const getMenuListForAllList = async isCache => {
        const res = await getMenuListForAll()
        // 限制仅在概览页面提示全量菜单
        const isSummary = location.href.includes('side/summary')
        if (
          !isMenuListSame(res, lodash.cloneDeep(this.originMenuListAll)) &&
          isCache &&
          isSummary
        ) {
          showUpdateInfo('全量菜单有更新')
        }
        this.originMenuListAll = res
        next(res)
        return true
      }
      try {
        if (this.originMenuListAll.length > 0 && !sessionStorage.getItem(reloadFlagKey)) {
          next(this.originMenuListAll)
          getMenuListForAllList(true)
          return true
        }
        await getMenuListForAllList()
        return true
      } catch (e) {
        console.error(e)
      } finally {
        sessionStorage.removeItem(reloadFlagKey)
      }
    },
    // 初始化当前菜单信息
    async initCurrentSideList(key) {
      const allSideMenuMap = this.getAllSideMenuMap
      const list = allSideMenuMap[key]?.children
      if (Array.isArray(list)) {
        this.currentSideList = list
      }
    },
    // 切换国家时重置菜单信息
    switchCountry() {
      this.originMenuList = []
      this.originMenuListAll = []
    },
    clearStore() {
      this.originMenuList = []
      this.originMenuListAll = []
      localStorage.removeItem(storeKey)
    },
  },
})

export default useMenuStore

// 递归判断菜单列表是否相同
function isMenuListSame(newList, oldList) {
  if (newList.length !== oldList.length) {
    return false
  }
  // 如果长度不同，直接返回 false
  if (!newList || !oldList || newList.length !== oldList.length) {
    return false
  }

  // 递归比较每个菜单项
  for (let i = 0; i < newList.length; i++) {
    if (!isMenuItemSame(newList[i], oldList[i])) {
      return false
    }
  }

  return true
}
// 比较更多字段的版本
function isMenuItemSame(newItem, oldItem) {
  if (!newItem || !oldItem) {
    return false
  }
  // isApply
  // isAuth
  // isHide
  // key
  // name
  // nodeId
  // nodeType
  // remark
  // 比较多个字段
  const fieldsToCompare = ['key', 'nodeId', 'isHide', 'isAuth'] // 根据需要添加字段
  for (const field of fieldsToCompare) {
    if (newItem[field] !== oldItem[field]) {
      return false
    }
  }

  // 递归比较子节点
  if (newItem.sonNodes && oldItem.sonNodes) {
    return isMenuListSame(newItem.sonNodes, oldItem.sonNodes)
  }

  if (newItem.sonNodes || oldItem.sonNodes) {
    return false
  }

  return true
}
